/**
 * Deadline badges (21d, brief 21 §1 "Deadline"): one badge for a business deadline on a work item, an activity, an
 * approval request or an obligation. Words carry the meaning (colour is never the only signal): the kind
 * ("Statutory" is always named, since it can never be moved at runtime), the time left or overdue, and
 * "breached while held" when the deadline passed during a hold (holds pause no clock, §1b.3).
 */
import type { Deadline } from '@agentic/contracts';
import { formatDateTime } from '../format.ts';
import { Badge, type Tone } from './primitives.tsx';

/** Due within this span counts as "due soon" (amber). */
export const DUE_SOON_MS = 24 * 3600_000;

export type DeadlineState = 'overdue' | 'due_soon' | 'due';

/** Where a deadline stands at `now` (`overdue` also when the server flags it). */
export function deadlineState(deadline: Pick<Deadline, 'at'>, now: number, overdue = false): DeadlineState {
  const left = Date.parse(deadline.at) - now;
  if (overdue || left <= 0) return 'overdue';
  return left <= DUE_SOON_MS ? 'due_soon' : 'due';
}

/** `2 d`, `5 h`, `12 min` (rounded down, at least one minute). */
export function spanText(ms: number): string {
  const abs = Math.abs(ms);
  if (abs >= 2 * 86_400_000) return `${Math.floor(abs / 86_400_000)} d`;
  if (abs >= 3_600_000) return `${Math.floor(abs / 3_600_000)} h`;
  return `${Math.max(1, Math.floor(abs / 60_000))} min`;
}

const KIND_WORDS: Readonly<Record<Deadline['kind'], string>> = {
  sla: 'SLA',
  contractual: 'Contractual',
  statutory: 'Statutory',
};

const STATE_TONES: Readonly<Record<DeadlineState, Tone>> = {
  overdue: 'bad',
  due_soon: 'warn',
  due: 'neutral',
};

/** One-line text of a deadline, e.g. `Statutory · overdue 2 h` or `SLA · due in 3 d`. */
export function deadlineText(deadline: Deadline, now: number, overdue = false): string {
  const state = deadlineState(deadline, now, overdue);
  const left = Date.parse(deadline.at) - now;
  const when = state === 'overdue' ? `overdue ${spanText(left)}` : `due in ${spanText(left)}`;
  return `${KIND_WORDS[deadline.kind]} · ${when}`;
}

export interface DeadlineBadgeProps {
  deadline: Deadline;
  /** Current time in ms (tests pin it). */
  now?: number;
  /** The server's overdue flag (work items). */
  overdue?: boolean;
  /** Set when the deadline breached during a hold (`DeadlineBreach.whileHeld`). */
  breachedWhileHeld?: { heldMs: number } | undefined;
  className?: string;
}

/** Badge of a business deadline: kind, time left or overdue, and the breached-while-held note. */
export function DeadlineBadge({
  deadline,
  now = Date.now(),
  overdue,
  breachedWhileHeld,
  className,
}: DeadlineBadgeProps) {
  const state = deadlineState(deadline, now, overdue);
  const text = deadlineText(deadline, now, overdue);
  const title = [
    `${KIND_WORDS[deadline.kind]} deadline ${formatDateTime(deadline.at)}`,
    deadline.basisRef ? `basis ${deadline.basisRef}` : undefined,
    deadline.calendarId ? `business days (${deadline.calendarId})` : undefined,
    deadline.kind === 'statutory' ? 'set by law or regulation; cannot be moved' : undefined,
  ]
    .filter(Boolean)
    .join(' · ');
  return (
    <Badge
      tone={STATE_TONES[state]}
      dot={state !== 'due'}
      title={title}
      className={className}
      data-testid="deadline-badge"
      data-kind={deadline.kind}
      data-state={state}
      data-statutory={deadline.kind === 'statutory' || undefined}
    >
      {deadline.kind === 'statutory' && (
        <span aria-hidden className="font-semibold">
          §{' '}
        </span>
      )}
      {text}
      {breachedWhileHeld && (
        <span data-testid="breached-while-held">
          {' '}
          · breached while held ({spanText(breachedWhileHeld.heldMs)} held)
        </span>
      )}
    </Badge>
  );
}
